import { DEFAULT_LOCALE, SupportedLocale } from "./locales";

type TermsSection = {
  title: string;
  paragraphs: string[];
  list?: string[];
};

type TermsCopy = {
  title: string;
  effectiveDate: string;
  sections: TermsSection[];
  closing: string;
  contactLabel: string;
  supportLinkLabel: string;
};

const termsCopy: Record<SupportedLocale, TermsCopy> = {
  en: {
    title: "Terms of Service for videototextai.com",
    effectiveDate: "Effective Date: 20.11.2023",
    closing: "By using the videototextai.com Service, you signify your agreement to these Terms of Service.",
    contactLabel: "If you have any questions, please contact us through",
    supportLinkLabel: "our support page",
    sections: [
      {
        title: "1. Introduction",
        paragraphs: [
          'These Terms of Service ("Terms") govern your use of the videototextai.com website and its associated transcription software services (collectively, the "Service"). By using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.',
        ],
      },
      {
        title: "2. Definitions",
        paragraphs: [],
        list: [
          "User Content: Any text, images, or other materials you upload to the Service for transcription.",
          "Anonymized Data: Data derived from User Content with personal identifiers removed.",
        ],
      },
      {
        title: "3. Use of Service",
        paragraphs: [],
        list: [
          "Eligibility: You must be at least 18 years old to use the Service. If you represent a business, you warrant that you have the authority to agree to these Terms on the business's behalf.",
          "Account Registration: You may need to create an account to use the Service. You are responsible for safeguarding your login information.",
          "Acceptable Use: You must use the Service responsibly and in compliance with all laws. You will not use it for any illegal, harmful, or unauthorized purposes.",
        ],
      },
      {
        title: "4. Intellectual Property",
        paragraphs: [],
        list: [
          "Service Content: videototextai.com or its licensors own all content on the Service, protected by intellectual property laws. You may not copy or distribute Service content without our permission.",
          "User Content: You retain ownership of your User Content. By submitting content, you grant videototextai.com a non-exclusive, worldwide, perpetual, royalty-free license to use, copy, modify, and display your User Content in order to provide the Service. videototextai.com may also use Anonymized Data for Service improvement and analytics.",
        ],
      },
      {
        title: "5. Payment Terms",
        paragraphs: [],
        list: [
          "Provider: All of the Service's payment processing is handled by Stripe. By using the Service, you agree to be bound by Stripe's terms and conditions.",
          "Payment Methods: Payment methods available are based on the country you are located in. You can find the available payment methods on the checkout page.",
          "Refund Policy: Refunds are available only if you have not used the Service and request a refund within 30 days of purchase. If you have used the Service, you will not be eligible for a refund for the credits used. The only exceptions are technical issues on our side. Please contact us at info@videototextai.com to request a refund.",
        ],
      },
      {
        title: "6. User Responsibilities",
        paragraphs: [],
        list: [
          "You are responsible for all activity under your account.",
          "You must comply with all applicable laws and regulations when using the Service.",
          "You will respect the intellectual property rights of others.",
        ],
      },
      {
        title: "7. Disclaimers and Limitation of Liability",
        paragraphs: [
          'NO WARRANTIES: The Service is provided "AS IS" without warranties of any kind. We do not guarantee the accuracy or completeness of transcription results.',
          "LIMITATION OF LIABILITY: videototextai.com will not be liable for any damages arising from your use of the Service, except as required by law.",
        ],
      },
      {
        title: "8. Termination",
        paragraphs: [
          "We may terminate your account at any time for any reason, with or without notice. Upon termination, your right to use the Service will cease.",
        ],
      },
      {
        title: "9. Governing Law",
        paragraphs: ["These Terms are governed by the laws of Estonia, without regard to conflict of laws principles."],
      },
      {
        title: "10. Changes to Terms",
        paragraphs: [
          "We may modify these Terms at any time. We'll notify you via email or a prominent notice on our website. Your continued use of the Service after changes take effect signifies your acceptance of the new Terms.",
        ],
      },
      {
        title: "11. Dispute Resolution",
        paragraphs: ["Any disputes arising from these Terms will be resolved through legal methods in the courts of Estonia."],
      },
      {
        title: "12. Marketing and Communications",
        paragraphs: [
          "By creating an account and using the videototextai.com Service, you consent to receive promotional and marketing materials related to our products, services, and features. We may use your contact information, such as your email address, to send you updates, offers, and other information that we believe may be of interest to you.",
          "You can opt out of receiving these marketing communications at any time by following the unsubscribe instructions provided in the emails or by contacting us directly through our support page.",
        ],
      },
      {
        title: "13. Contact Us",
        paragraphs: [],
      },
    ],
  },
  es: {
    title: "Términos de servicio de videototextai.com",
    effectiveDate: "Fecha de vigencia: 20.11.2023",
    closing: "Al usar el servicio de videototextai.com, confirmas tu acuerdo con estos Términos de servicio.",
    contactLabel: "Si tienes preguntas, contáctanos a través de",
    supportLinkLabel: "nuestra página de soporte",
    sections: [
      {
        title: "1. Introducción",
        paragraphs: [
          'Estos Términos de servicio ("Términos") regulan tu uso del sitio web videototextai.com y sus servicios de software de transcripción asociados (en conjunto, el "Servicio"). Al usar el Servicio, aceptas estos Términos. Si no estás de acuerdo, no uses el Servicio.',
        ],
      },
      {
        title: "2. Definiciones",
        paragraphs: [],
        list: [
          "Contenido del usuario: cualquier texto, imagen u otro material que subas al Servicio para transcripción.",
          "Datos anonimizados: datos derivados del contenido del usuario sin identificadores personales.",
        ],
      },
      {
        title: "3. Uso del Servicio",
        paragraphs: [],
        list: [
          "Elegibilidad: debes tener al menos 18 años para usar el Servicio. Si representas a una empresa, garantizas tener autoridad para aceptar estos Términos.",
          "Registro de cuenta: puede ser necesario crear una cuenta. Eres responsable de proteger tus credenciales.",
          "Uso aceptable: debes usar el Servicio de forma responsable y conforme a la ley. No lo usarás para fines ilegales, perjudiciales o no autorizados.",
        ],
      },
      {
        title: "4. Propiedad intelectual",
        paragraphs: [],
        list: [
          "Contenido del Servicio: videototextai.com o sus licenciantes poseen el contenido del Servicio. No puedes copiarlo o distribuirlo sin permiso.",
          "Contenido del usuario: conservas la propiedad de tu contenido. Al enviarlo, otorgas a videototextai.com una licencia no exclusiva, mundial, perpetua y libre de regalías para usarlo, copiarlo, modificarlo y mostrarlo con el fin de prestar el Servicio. videototextai.com también puede usar datos anonimizados para mejorar el Servicio y realizar analítica.",
        ],
      },
      {
        title: "5. Términos de pago",
        paragraphs: [],
        list: [
          "Proveedor: el procesamiento de pagos lo gestiona Stripe. Al usar el Servicio, aceptas los términos de Stripe.",
          "Métodos de pago: dependen del país en el que te encuentres. Puedes verlos en la página de pago.",
          "Política de reembolso: los reembolsos solo aplican si no has usado el Servicio y solicitas el reembolso dentro de 30 días. Si has usado el Servicio, no eres elegible para el reembolso de los créditos usados. Las únicas excepciones son problemas técnicos de nuestra parte. Contacta a info@videototextai.com para solicitar un reembolso.",
        ],
      },
      {
        title: "6. Responsabilidades del usuario",
        paragraphs: [],
        list: [
          "Eres responsable de toda la actividad en tu cuenta.",
          "Debes cumplir con todas las leyes y regulaciones aplicables al usar el Servicio.",
          "Respetarás los derechos de propiedad intelectual de terceros.",
        ],
      },
      {
        title: "7. Exenciones y limitación de responsabilidad",
        paragraphs: [
          'SIN GARANTÍAS: el Servicio se proporciona "TAL CUAL" sin garantías de ningún tipo. No garantizamos la exactitud o integridad de las transcripciones.',
          "LIMITACIÓN DE RESPONSABILIDAD: videototextai.com no será responsable por daños derivados del uso del Servicio, salvo lo requerido por ley.",
        ],
      },
      {
        title: "8. Terminación",
        paragraphs: [
          "Podemos terminar tu cuenta en cualquier momento por cualquier motivo, con o sin aviso. Al terminar, cesa tu derecho de uso del Servicio.",
        ],
      },
      {
        title: "9. Ley aplicable",
        paragraphs: ["Estos Términos se rigen por las leyes de Estonia, sin considerar principios de conflicto de leyes."],
      },
      {
        title: "10. Cambios en los Términos",
        paragraphs: [
          "Podemos modificar estos Términos en cualquier momento. Te notificaremos por email o mediante un aviso destacado en nuestro sitio. El uso continuo del Servicio después de que los cambios entren en vigor implica tu aceptación de los nuevos Términos.",
        ],
      },
      {
        title: "11. Resolución de disputas",
        paragraphs: ["Cualquier disputa derivada de estos Términos se resolverá por vías legales en los tribunales de Estonia."],
      },
      {
        title: "12. Marketing y comunicaciones",
        paragraphs: [
          "Al crear una cuenta y usar el Servicio de videototextai.com, aceptas recibir materiales promocionales y de marketing relacionados con nuestros productos, servicios y funciones. Podemos usar tu información de contacto (por ejemplo, tu email) para enviarte actualizaciones, ofertas y otra información que consideremos de tu interés.",
          "Puedes darte de baja de estas comunicaciones en cualquier momento siguiendo las instrucciones de cancelación en los emails o contactándonos directamente a través de nuestra página de soporte.",
        ],
      },
      {
        title: "13. Contacto",
        paragraphs: [],
      },
    ],
  },
  fr: {
    title: "Conditions d'utilisation de videototextai.com",
    effectiveDate: "Date d'effet : 20.11.2023",
    closing: "En utilisant le service videototextai.com, vous acceptez ces Conditions d'utilisation.",
    contactLabel: "Si vous avez des questions, veuillez nous contacter via",
    supportLinkLabel: "notre page de support",
    sections: [
      {
        title: "1. Introduction",
        paragraphs: [
          'Les présentes Conditions d\'utilisation ("Conditions") régissent votre utilisation du site videototextai.com et de ses services logiciels de transcription (collectivement, le "Service"). En utilisant le Service, vous acceptez ces Conditions. Si vous n\'êtes pas d\'accord, veuillez ne pas utiliser le Service.',
        ],
      },
      {
        title: "2. Définitions",
        paragraphs: [],
        list: [
          "Contenu utilisateur : tout texte, image ou autre matériel que vous téléversez sur le Service pour transcription.",
          "Données anonymisées : données dérivées du contenu utilisateur dont les identifiants personnels ont été supprimés.",
        ],
      },
      {
        title: "3. Utilisation du Service",
        paragraphs: [],
        list: [
          "Éligibilité : vous devez avoir au moins 18 ans. Si vous représentez une entreprise, vous garantissez avoir l'autorité d'accepter ces Conditions.",
          "Inscription : vous devrez peut-être créer un compte. Vous êtes responsable de protéger vos identifiants.",
          "Utilisation acceptable : vous devez utiliser le Service de manière responsable et conforme à la loi. Aucun usage illégal, nuisible ou non autorisé.",
        ],
      },
      {
        title: "4. Propriété intellectuelle",
        paragraphs: [],
        list: [
          "Contenu du Service : videototextai.com ou ses concédants détiennent le contenu du Service, protégé par les lois de propriété intellectuelle. Vous ne pouvez pas copier ou distribuer ce contenu sans autorisation.",
          "Contenu utilisateur : vous conservez la propriété de votre contenu. En le soumettant, vous accordez à videototextai.com une licence non exclusive, mondiale, perpétuelle et gratuite pour utiliser, copier, modifier et afficher votre contenu afin de fournir le Service. videototextai.com peut également utiliser des données anonymisées à des fins d'amélioration du Service et d'analytique.",
        ],
      },
      {
        title: "5. Conditions de paiement",
        paragraphs: [],
        list: [
          "Fournisseur : le traitement des paiements est assuré par Stripe. En utilisant le Service, vous acceptez les conditions de Stripe.",
          "Moyens de paiement : ils dépendent du pays. Consultez la page de paiement.",
          "Politique de remboursement : les remboursements ne sont possibles que si le Service n'a pas été utilisé et si la demande est faite dans les 30 jours. Si le Service a été utilisé, aucun remboursement des crédits utilisés. Les seules exceptions concernent des problèmes techniques de notre part. Contactez info@videototextai.com pour demander un remboursement.",
        ],
      },
      {
        title: "6. Responsabilités de l'utilisateur",
        paragraphs: [],
        list: [
          "Vous êtes responsable de toute activité sur votre compte.",
          "Vous devez respecter toutes les lois et réglementations applicables lors de l'utilisation du Service.",
          "Vous respecterez les droits de propriété intellectuelle de tiers.",
        ],
      },
      {
        title: "7. Exclusions et limitation de responsabilité",
        paragraphs: [
          "AUCUNE GARANTIE : le Service est fourni \"TEL QUEL\" sans garantie. Nous ne garantissons pas l'exactitude ou l'exhaustivité des transcriptions.",
          "LIMITATION DE RESPONSABILITÉ : videototextai.com ne pourra être tenu responsable des dommages résultant de votre utilisation du Service, sauf obligation légale.",
        ],
      },
      {
        title: "8. Résiliation",
        paragraphs: [
          "Nous pouvons résilier votre compte à tout moment, pour quelque raison que ce soit, avec ou sans préavis. En cas de résiliation, votre droit d'utiliser le Service cessera.",
        ],
      },
      {
        title: "9. Droit applicable",
        paragraphs: ["Ces Conditions sont régies par les lois de l'Estonie, sans tenir compte des règles de conflit de lois."],
      },
      {
        title: "10. Modifications des Conditions",
        paragraphs: [
          "Nous pouvons modifier ces Conditions à tout moment. Nous vous informerons par email ou via un avis visible sur notre site. Votre utilisation continue après l'entrée en vigueur des modifications vaut acceptation des nouvelles Conditions.",
        ],
      },
      {
        title: "11. Résolution des litiges",
        paragraphs: ["Tout litige découlant de ces Conditions sera résolu par des voies légales devant les tribunaux d'Estonie."],
      },
      {
        title: "12. Marketing et communications",
        paragraphs: [
          "En créant un compte et en utilisant le Service de videototextai.com, vous acceptez de recevoir des communications marketing et promotionnelles liées à nos produits, services et fonctionnalités. Nous pouvons utiliser vos coordonnées (par exemple votre email) pour vous envoyer des mises à jour, des offres et d'autres informations susceptibles de vous intéresser.",
          "Vous pouvez vous désinscrire à tout moment en suivant les instructions dans les emails ou en nous contactant via notre page de support.",
        ],
      },
      {
        title: "13. Contact",
        paragraphs: [],
      },
    ],
  },
  de: {
    title: "Nutzungsbedingungen für videototextai.com",
    effectiveDate: "Gültig ab: 20.11.2023",
    closing: "Durch die Nutzung des videototextai.com Service stimmen Sie diesen Nutzungsbedingungen zu.",
    contactLabel: "Wenn Sie Fragen haben, kontaktieren Sie uns bitte über",
    supportLinkLabel: "unsere Support-Seite",
    sections: [
      {
        title: "1. Einleitung",
        paragraphs: [
          'Diese Nutzungsbedingungen ("Bedingungen") regeln Ihre Nutzung der Website videototextai.com und der zugehörigen Transkriptionssoftware-Dienste (zusammen der "Service"). Durch die Nutzung stimmen Sie diesen Bedingungen zu. Wenn Sie nicht zustimmen, nutzen Sie den Service nicht.',
        ],
      },
      {
        title: "2. Definitionen",
        paragraphs: [],
        list: [
          "Nutzerinhalte: Texte, Bilder oder andere Materialien, die Sie für die Transkription hochladen.",
          "Anonymisierte Daten: Daten aus Nutzerinhalten ohne personenbezogene Kennzeichen.",
        ],
      },
      {
        title: "3. Nutzung des Services",
        paragraphs: [],
        list: [
          "Berechtigung: Sie müssen mindestens 18 Jahre alt sein. Wenn Sie ein Unternehmen vertreten, garantieren Sie die entsprechende Befugnis.",
          "Kontoregistrierung: Möglicherweise ist ein Konto erforderlich. Sie sind für den Schutz Ihrer Zugangsdaten verantwortlich.",
          "Zulässige Nutzung: Der Service ist verantwortungsvoll und gesetzeskonform zu nutzen. Keine illegalen, schädlichen oder nicht autorisierten Zwecke.",
        ],
      },
      {
        title: "4. Geistiges Eigentum",
        paragraphs: [],
        list: [
          "Service-Inhalte: videototextai.com oder Lizenzgeber besitzen alle Inhalte des Services, geschützt durch Gesetze zum geistigen Eigentum. Keine Vervielfältigung oder Verbreitung ohne Erlaubnis.",
          "Nutzerinhalte: Sie behalten das Eigentum an Ihren Nutzerinhalten. Durch das Einreichen gewähren Sie videototextai.com eine nicht exklusive, weltweite, unbefristete und gebührenfreie Lizenz, Ihre Nutzerinhalte zu verwenden, zu kopieren, zu verändern und anzuzeigen, um den Service bereitzustellen. Anonymisierte Daten können zudem zur Verbesserung des Services und für Analysen verwendet werden.",
        ],
      },
      {
        title: "5. Zahlungsbedingungen",
        paragraphs: [],
        list: [
          "Anbieter: Zahlungen werden über Stripe abgewickelt. Durch die Nutzung stimmen Sie den Stripe-Bedingungen zu.",
          "Zahlungsmethoden: Abhängig vom Land. Verfügbare Methoden finden Sie auf der Checkout-Seite.",
          "Rückerstattung: Nur möglich, wenn der Service nicht genutzt wurde und die Anfrage innerhalb von 30 Tagen erfolgt. Keine Erstattung für genutzte Credits. Ausnahmen gelten nur bei technischen Problemen auf unserer Seite. Kontakt: info@videototextai.com.",
        ],
      },
      {
        title: "6. Pflichten des Nutzers",
        paragraphs: [],
        list: [
          "Sie sind für alle Aktivitäten unter Ihrem Konto verantwortlich.",
          "Sie müssen alle geltenden Gesetze und Vorschriften bei der Nutzung des Services einhalten.",
          "Sie werden die Rechte am geistigen Eigentum Dritter respektieren.",
        ],
      },
      {
        title: "7. Haftungsausschluss und Haftungsbegrenzung",
        paragraphs: [
          'KEINE GARANTIEN: Der Service wird "WIE BESEHEN" bereitgestellt. Wir garantieren weder Genauigkeit noch Vollständigkeit der Transkriptionen.',
          "HAFTUNGSBEGRENZUNG: videototextai.com haftet nicht für Schäden aus der Nutzung des Services, außer soweit gesetzlich vorgeschrieben.",
        ],
      },
      {
        title: "8. Kündigung",
        paragraphs: [
          "Wir können Ihr Konto jederzeit aus beliebigem Grund, mit oder ohne Vorankündigung, kündigen. Nach der Kündigung endet Ihr Nutzungsrecht.",
        ],
      },
      {
        title: "9. Anwendbares Recht",
        paragraphs: ["Diese Bedingungen unterliegen dem Recht Estlands, ohne Berücksichtigung von Kollisionsnormen."],
      },
      {
        title: "10. Änderungen der Bedingungen",
        paragraphs: [
          "Wir können diese Bedingungen jederzeit ändern. Wir informieren Sie per E-Mail oder durch einen deutlichen Hinweis auf der Website. Die weitere Nutzung nach Inkrafttreten gilt als Zustimmung zu den neuen Bedingungen.",
        ],
      },
      {
        title: "11. Streitbeilegung",
        paragraphs: ["Streitigkeiten aus diesen Bedingungen werden auf dem Rechtsweg vor den Gerichten Estlands geklärt."],
      },
      {
        title: "12. Marketing und Kommunikation",
        paragraphs: [
          "Durch das Erstellen eines Kontos und die Nutzung des videototextai.com Services stimmen Sie zu, Marketing- und Werbematerialien zu unseren Produkten, Services und Funktionen zu erhalten. Wir können Ihre Kontaktdaten (z. B. E-Mail-Adresse) nutzen, um Ihnen Updates, Angebote und weitere Informationen zu senden.",
          "Sie können sich jederzeit abmelden, indem Sie den Abmeldelink in den E-Mails nutzen oder uns über unsere Support-Seite kontaktieren.",
        ],
      },
      {
        title: "13. Kontakt",
        paragraphs: [],
      },
    ],
  },
  it: {
    title: "Termini di servizio di videototextai.com",
    effectiveDate: "Data di efficacia: 20.11.2023",
    closing: "Utilizzando il servizio videototextai.com, accetti questi Termini di servizio.",
    contactLabel: "Se hai domande, contattaci tramite",
    supportLinkLabel: "la nostra pagina di supporto",
    sections: [
      {
        title: "1. Introduzione",
        paragraphs: [
          'I presenti Termini di servizio ("Termini") regolano l\'uso del sito videototextai.com e dei servizi software di trascrizione (collettivamente, il "Servizio"). Utilizzando il Servizio, accetti questi Termini. Se non accetti, non utilizzare il Servizio.',
        ],
      },
      {
        title: "2. Definizioni",
        paragraphs: [],
        list: [
          "Contenuto dell'utente: qualsiasi testo, immagine o altro materiale caricato sul Servizio per la trascrizione.",
          "Dati anonimizzati: dati derivati dal contenuto dell'utente con identificatori personali rimossi.",
        ],
      },
      {
        title: "3. Uso del Servizio",
        paragraphs: [],
        list: [
          "Idoneità: devi avere almeno 18 anni per usare il Servizio. Se rappresenti un'azienda, garantisci di avere l'autorità per accettare questi Termini.",
          "Registrazione: potresti dover creare un account. Sei responsabile della sicurezza delle credenziali.",
          "Uso accettabile: devi usare il Servizio in modo responsabile e conforme alla legge. Non lo userai per scopi illegali, dannosi o non autorizzati.",
        ],
      },
      {
        title: "4. Proprietà intellettuale",
        paragraphs: [],
        list: [
          "Contenuto del Servizio: videototextai.com o i suoi licenzianti detengono i contenuti del Servizio, protetti dalle leggi sulla proprietà intellettuale. Non puoi copiare o distribuire i contenuti senza autorizzazione.",
          "Contenuto dell'utente: mantieni la proprietà del tuo contenuto. Inviandolo, concedi a videototextai.com una licenza non esclusiva, mondiale, perpetua e gratuita per usare, copiare, modificare e mostrare il tuo contenuto al fine di fornire il Servizio. videototextai.com può anche usare Dati anonimizzati per migliorare il Servizio e per analisi.",
        ],
      },
      {
        title: "5. Termini di pagamento",
        paragraphs: [],
        list: [
          "Fornitore: l'elaborazione dei pagamenti è gestita da Stripe. Usando il Servizio, accetti i termini e le condizioni di Stripe.",
          "Metodi di pagamento: dipendono dal paese in cui ti trovi. Puoi vedere i metodi disponibili nella pagina di checkout.",
          "Politica di rimborso: i rimborsi sono disponibili solo se non hai usato il Servizio e richiedi un rimborso entro 30 giorni dall'acquisto. Se hai usato il Servizio, non avrai diritto al rimborso dei crediti usati. Le uniche eccezioni sono problemi tecnici da parte nostra. Contattaci a info@videototextai.com per richiedere un rimborso.",
        ],
      },
      {
        title: "6. Responsabilità dell'utente",
        paragraphs: [],
        list: [
          "Sei responsabile di tutte le attività svolte tramite il tuo account.",
          "Devi rispettare tutte le leggi e normative applicabili quando usi il Servizio.",
          "Rispetterai i diritti di proprietà intellettuale di terzi.",
        ],
      },
      {
        title: "7. Esclusioni e limitazione di responsabilità",
        paragraphs: [
          "NESSUNA GARANZIA: il Servizio è fornito \"COSÌ COM'È\" senza garanzie di alcun tipo. Non garantiamo l'accuratezza o la completezza dei risultati di trascrizione.",
          "LIMITAZIONE DI RESPONSABILITÀ: videototextai.com non sarà responsabile per danni derivanti dall'uso del Servizio, salvo quanto richiesto dalla legge.",
        ],
      },
      {
        title: "8. Risoluzione",
        paragraphs: [
          "Possiamo terminare il tuo account in qualsiasi momento per qualsiasi motivo, con o senza preavviso. Alla risoluzione, il tuo diritto di usare il Servizio cesserà.",
        ],
      },
      {
        title: "9. Legge applicabile",
        paragraphs: ["Questi Termini sono regolati dalle leggi dell'Estonia, senza considerare i principi sul conflitto di leggi."],
      },
      {
        title: "10. Modifiche ai Termini",
        paragraphs: [
          "Possiamo modificare questi Termini in qualsiasi momento. Ti informeremo via email o con un avviso ben visibile sul nostro sito. L'uso continuato del Servizio dopo l'entrata in vigore delle modifiche implica la tua accettazione dei nuovi Termini.",
        ],
      },
      {
        title: "11. Risoluzione delle controversie",
        paragraphs: ["Eventuali controversie derivanti da questi Termini saranno risolte per vie legali nei tribunali dell'Estonia."],
      },
      {
        title: "12. Marketing e comunicazioni",
        paragraphs: [
          "Creando un account e utilizzando il servizio videototextai.com, acconsenti a ricevere comunicazioni promozionali e di marketing relative ai nostri prodotti, servizi e funzionalità. Possiamo usare le tue informazioni di contatto (come l'email) per inviarti aggiornamenti, offerte e altre informazioni che riteniamo possano interessarti.",
          "Puoi annullare l'iscrizione a queste comunicazioni in qualsiasi momento seguendo le istruzioni presenti nelle email o contattandoci tramite la nostra pagina di supporto.",
        ],
      },
      {
        title: "13. Contatti",
        paragraphs: [],
      },
    ],
  },
  pt: {
    title: "Termos de serviço do videototextai.com",
    effectiveDate: "Data de vigência: 20.11.2023",
    closing: "Ao usar o serviço do videototextai.com, você concorda com estes Termos de serviço.",
    contactLabel: "Se tiver alguma dúvida, entre em contato por",
    supportLinkLabel: "nossa página de suporte",
    sections: [
      {
        title: "1. Introdução",
        paragraphs: [
          'Estes Termos de serviço ("Termos") regem o uso do site videototextai.com e seus serviços de software de transcrição associados (coletivamente, o "Serviço"). Ao usar o Serviço, você concorda com estes Termos. Se não concordar, não use o Serviço.',
        ],
      },
      {
        title: "2. Definições",
        paragraphs: [],
        list: [
          "Conteúdo do usuário: qualquer texto, imagem ou outro material que você envia ao Serviço para transcrição.",
          "Dados anonimizados: dados derivados do conteúdo do usuário com identificadores pessoais removidos.",
        ],
      },
      {
        title: "3. Uso do Serviço",
        paragraphs: [],
        list: [
          "Elegibilidade: você deve ter pelo menos 18 anos para usar o Serviço. Se você representa uma empresa, garante que tem autoridade para aceitar estes Termos em nome dela.",
          "Registro de conta: pode ser necessário criar uma conta. Você é responsável por proteger suas credenciais.",
          "Uso aceitável: você deve usar o Serviço de forma responsável e conforme a lei. Você não o usará para fins ilegais, prejudiciais ou não autorizados.",
        ],
      },
      {
        title: "4. Propriedade intelectual",
        paragraphs: [],
        list: [
          "Conteúdo do Serviço: videototextai.com ou seus licenciadores detêm todo o conteúdo do Serviço, protegido por leis de propriedade intelectual. Você não pode copiar ou distribuir sem permissão.",
          "Conteúdo do usuário: você mantém a propriedade do seu conteúdo. Ao enviar conteúdo, você concede à videototextai.com uma licença não exclusiva, mundial, perpétua e gratuita para usar, copiar, modificar e exibir seu conteúdo para fornecer o Serviço. videototextai.com também pode usar Dados anonimizados para melhorias e análises do Serviço.",
        ],
      },
      {
        title: "5. Termos de pagamento",
        paragraphs: [],
        list: [
          "Fornecedor: o processamento de pagamentos é feito pela Stripe. Ao usar o Serviço, você concorda com os termos e condições da Stripe.",
          "Métodos de pagamento: variam conforme o país em que você está. Você pode ver os métodos disponíveis na página de checkout.",
          "Política de reembolso: reembolsos só estão disponíveis se você não tiver usado o Serviço e solicitar em até 30 dias após a compra. Se você tiver usado o Serviço, não terá direito a reembolso pelos créditos usados. As únicas exceções são problemas técnicos do nosso lado. Entre em contato pelo email info@videototextai.com para solicitar um reembolso.",
        ],
      },
      {
        title: "6. Responsabilidades do usuário",
        paragraphs: [],
        list: [
          "Você é responsável por toda atividade realizada na sua conta.",
          "Você deve cumprir todas as leis e regulamentos aplicáveis ao usar o Serviço.",
          "Você respeitará os direitos de propriedade intelectual de terceiros.",
        ],
      },
      {
        title: "7. Isenções e limitação de responsabilidade",
        paragraphs: [
          'SEM GARANTIAS: o Serviço é fornecido "COMO ESTÁ" sem garantias de qualquer tipo. Não garantimos a precisão ou integridade dos resultados de transcrição.',
          "LIMITAÇÃO DE RESPONSABILIDADE: videototextai.com não será responsável por quaisquer danos decorrentes do uso do Serviço, exceto quando exigido por lei.",
        ],
      },
      {
        title: "8. Encerramento",
        paragraphs: [
          "Podemos encerrar sua conta a qualquer momento por qualquer motivo, com ou sem aviso. Após o encerramento, seu direito de usar o Serviço cessará.",
        ],
      },
      {
        title: "9. Lei aplicável",
        paragraphs: ["Estes Termos são regidos pelas leis da Estônia, sem considerar princípios de conflito de leis."],
      },
      {
        title: "10. Alterações nos Termos",
        paragraphs: [
          "Podemos modificar estes Termos a qualquer momento. Notificaremos você por email ou por um aviso em destaque no nosso site. O uso contínuo do Serviço após a entrada em vigor das alterações indica sua aceitação dos novos Termos.",
        ],
      },
      {
        title: "11. Resolução de disputas",
        paragraphs: ["Quaisquer disputas decorrentes destes Termos serão resolvidas por meios legais nos tribunais da Estônia."],
      },
      {
        title: "12. Marketing e comunicações",
        paragraphs: [
          "Ao criar uma conta e usar o serviço videototextai.com, você concorda em receber comunicações promocionais e de marketing relacionadas aos nossos produtos, serviços e funcionalidades. Podemos usar suas informações de contato (como seu email) para enviar atualizações, ofertas e outras informações que acreditamos ser do seu interesse.",
          "Você pode cancelar o recebimento dessas comunicações a qualquer momento seguindo as instruções de descadastro nos emails ou entrando em contato conosco por meio da nossa página de suporte.",
        ],
      },
      {
        title: "13. Contato",
        paragraphs: [],
      },
    ],
  },
};

export function getTermsCopy(locale: SupportedLocale): TermsCopy {
  return termsCopy[locale] ?? termsCopy[DEFAULT_LOCALE];
}
